import React, { useEffect, useState } from 'react';
import type { OrderLeg } from '../../services/orders.service';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';
import Toggle from '../Toggle';
import PermissionsContainer, { useResolvedAccessLevel } from '../PermissionsContainer';
import { PermissionAwareButton } from '../../contexts/PermissionAccessContext';
import DriverPayOpenLegsDrawer from '../tablemaint/DriverPayOpenLegsDrawer';
import LegDocumentModal from './LegDocumentModal';

/** Permission gating the editable surface of this modal (require dropdowns, Do Not Pay,
 *  and the Reject Paperwork action). One container per the reconciliation plan; "Remove Pay
 *  and Open Leg" keeps its own `leg-driverpay-clear`. Unconfigured => level 2 (edit), so
 *  behaviour is unchanged until an admin restricts it. */
const DISPATCH_INFO_EDIT_COMPONENT_ID = 'leg-dispatch-info-edit';

/** Leg fields whose values the modal can mutate. */
export type DispatchInfoEditableField =
  | 'donotpayleg'
  | 'origrequireinspection'
  | 'origrequireinterchange'
  | 'origrequirebol'
  | 'destrequirepod'
  | 'destrequireinterchange'
  | 'destrequirescaleticket';

/** Stored values for the require* dropdowns. Legacy CMS uses the all-caps
 *  string 'RESOLVED' — the dropdown renders it title-cased but the wire
 *  value stays uppercase. donotpayleg stays Y/N. */
type RequireValue = 'Y' | 'N' | 'RESOLVED';
const REQUIRE_OPTIONS: ReadonlyArray<RequireValue> = ['Y', 'N', 'RESOLVED'];

const formatRequireLabel = (v: string): string =>
  v === 'RESOLVED' ? 'Resolved' : v;

export interface DispatchInfoModalProps {
  leg: OrderLeg | null;
  onClose: () => void;
  /** Optional editor callback. When provided, the require* rows become Y/N/
   *  Resolved dropdowns and the "Do Not Pay" row becomes a toggle. Each
   *  change is pushed up via onChange(legrecno, field, value); writes don't
   *  persist until the parent saves the pro. When omitted the modal stays
   *  read-only (preserves the original behaviour for callers that don't
   *  wire editing). */
  onChange?: (legrecno: number, field: DispatchInfoEditableField, value: string) => void;
  /** Fired after the leg's driver-pay line is cleared via "Remove Pay and Open
   *  Leg" (Clear Leg). Unlike the require/donotpay edits above, that action
   *  persists immediately, so the parent should refetch the pro to pick up the
   *  now-blanked pay line. When omitted, the Clear Leg affordance is hidden
   *  (there'd be no way to refresh the stale data). */
  onPayCleared?: () => void;
  /** Fired after a leg document's paperwork is rejected. Like Clear Leg, the reject
   *  persists immediately (it flips the leg's require flag back to Y and clears the doc
   *  recno server-side), so the parent should refetch the pro. When omitted, the Reject
   *  action is unavailable (there'd be no way to refresh the stale data). */
  onDocRejected?: () => void;
}

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex justify-between">
    <span className="text-muted">{label}</span>
    <span className="text-heading">{value || '-'}</span>
  </div>
);

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-slate-600 dark:bg-slate-800 text-white text-xs font-semibold px-2 py-1 rounded mb-1.5">
    {children}
  </div>
);

const ViewDocIcon: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={`View ${label.replace(/:$/, '')} document`}
    title="View document"
    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
  >
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  </button>
);

/** Editable Y/N/Resolved dropdown row used for the six require* fields. Falls
 *  back to a read-only value when not editable. Shows a View-document icon
 *  whenever the row's document recno is set (a real legdocumentinfo record
 *  exists — v1 documentlink "View"). Maintains its own local mirror state so
 *  the select stays sticky after a pick — the parent re-renders LegsGrid but
 *  does not refresh the snapshotted leg held by the modal. Tolerates an unknown
 *  incoming value by prepending it to the options list. */
const RequireSelectRow: React.FC<{
  label: string;
  value: string | null | undefined;
  field: DispatchInfoEditableField;
  legrecno: number;
  editable: boolean;
  onChange?: (legrecno: number, field: DispatchInfoEditableField, value: string) => void;
  docRecno?: number;
  onViewDocument?: (recno: number) => void;
}> = ({ label, value, field, legrecno, editable, onChange, docRecno, onViewDocument }) => {
  const [v, setV] = useState<string>((value || 'N') as string);
  useEffect(() => {
    setV((value || 'N') as string);
  }, [legrecno, value]);

  const viewIcon =
    docRecno && docRecno > 0 && onViewDocument ? (
      <ViewDocIcon label={label} onClick={() => onViewDocument(docRecno)} />
    ) : null;

  if (!editable || !onChange) {
    return (
      <div className="flex justify-between items-center">
        <span className="text-muted">{label}</span>
        <span className="flex items-center gap-1.5">
          {viewIcon}
          <span className="text-heading">{formatRequireLabel(v)}</span>
        </span>
      </div>
    );
  }
  const opts: string[] = REQUIRE_OPTIONS.includes(v as RequireValue)
    ? [...REQUIRE_OPTIONS]
    : [v, ...REQUIRE_OPTIONS];
  return (
    <div className="flex justify-between items-center">
      <span className="text-muted">{label}</span>
      <span className="flex items-center gap-1.5">
        {viewIcon}
        <select
          value={v}
          onChange={(e) => {
            const next = e.target.value;
            setV(next);
            onChange(legrecno, field, next);
          }}
          aria-label={label.replace(/:$/, '')}
          className="h-5 px-1 py-0 text-[0.6875rem] rounded border border-slate-300 bg-white dark:bg-gray-700 text-slate-900 dark:text-white cursor-pointer focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20"
        >
          {opts.map((o) => (
            <option key={o} value={o}>
              {formatRequireLabel(o)}
            </option>
          ))}
        </select>
      </span>
    </div>
  );
};

const DispatchInfoModal: React.FC<DispatchInfoModalProps> = ({ leg, onClose, onChange, onPayCleared, onDocRejected }) => {
  // Local mirror of donotpayleg so the toggle reacts instantly while the
  // parent's editing layer catches up. Resets whenever the modal opens for
  // a different leg.
  const [donotpay, setDonotpay] = useState<'Y' | 'N'>(
    ((leg?.donotpayleg as 'Y' | 'N') || 'N'),
  );
  // Whether the reused "Clear Leg" drawer (DriverPayOpenLegsDrawer) is open on
  // top of this modal. Resets when the modal opens for a different leg.
  const [showClearDrawer, setShowClearDrawer] = useState(false);
  // legdocumentinfo recno currently open in the View/Reject modal (null = closed).
  const [docViewRecno, setDocViewRecno] = useState<number | null>(null);
  useEffect(() => {
    if (leg) setDonotpay(((leg.donotpayleg as 'Y' | 'N') || 'N'));
    setShowClearDrawer(false);
    setDocViewRecno(null);
  }, [leg?.legrecno, leg?.donotpayleg]);

  // Edit permission for this modal's controls. Unconfigured => level 2 (edit).
  const editAccess = useResolvedAccessLevel(DISPATCH_INFO_EDIT_COMPONENT_ID);
  const canEdit = editAccess >= 2;

  // Alt+C closes; edits here save through their own inline controls, so there's
  // no single Save to bind. Must sit ABOVE the `!leg` guard — this modal stays
  // mounted with leg=null, so a hook below the guard would change the hook count
  // when the leg arrives (React #310). `layer: !!leg` is what makes it inert
  // while closed.
  useAltHotkeys({ C: onClose }, undefined, { layer: !!leg });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(!!leg);

  if (!leg) return null;
  const sendToGwiMove = !!leg.sendToGwiMove;

  // Require dropdowns / Do Not Pay are editable only while the order is in edit mode
  // (onChange wired) AND the user has edit permission for this modal.
  const requireEditable = !!onChange && canEdit;
  // Reject persists immediately, so it needs both edit permission and the refresh
  // callback (mirrors Clear Leg's onPayCleared requirement).
  const canReject = canEdit && !!onDocRejected;
  // Legacy driver-present/empty split (main.pl driverstatusinfo): pay rows when the
  // PAY LINE has a driver, the "Do Not Pay?" row when not. This keys on the non-deleted
  // driverpay_lineitem's driver (v1 dpay->driver) — NOT the leg's assigned driver — so a
  // leg that has an assigned driver but no pay line yet still shows "Do Not Pay?".
  const hasPayLineDriver = !!(leg.driverPayDriver ?? '').trim();

  // Payment Requirements visibility (match legacy exactly): sent to gwiMove OR the assigned
  // driver is a gwiMove driver and the leg is not yet invoiced (`invoicedate eq ''`).
  const showPaymentReq = sendToGwiMove || (!!leg.driverUsesGwiMove && !leg.isPaid);

  // "Remove Pay and Open Leg" (Clear Leg) eligibility, mirroring v1
  // driverstatusinfo: order handled by a GWI dispatcher, the pay line is not
  // yet invoiced (allowedit), and we have a pay-line recno + a refresh callback.
  const canClearPay =
    !!onPayCleared &&
    !!leg.orderIsGwiDispatcher &&
    !!leg.driverPayAllowEdit &&
    !!leg.driverPayRecno;

  return (
    <>
      <div className="fixed inset-0 bg-scrim/30 z-50" onClick={onClose} aria-label="Close dispatch info" />
      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      <div
        role="dialog"
        aria-label={`Dispatch details for leg ${leg.legnum}`}
        ref={panelRef}
        style={panelStyle}
        className="pointer-events-auto w-[400px] max-h-[80vh] overflow-y-auto bg-panel rounded-lg shadow-xl border border-gray-200 dark:border-gray-700"
      >
        <ModalHeader
          title={`Dispatch Details (Leg ${leg.legnum})`}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />

        <div className="p-4 text-xs space-y-4">
          <div className="space-y-1">
            <Row label="Dispatch Date/Time:" value={leg.dspdriverdatetime} />
            {sendToGwiMove && <Row label="Accept Date/Time:" value={leg.acceptdatetime} />}
          </div>

          {sendToGwiMove && (
            <div>
              <SectionHeader>Geofence Information</SectionHeader>
              <div className="space-y-1">
                <Row label="Origin Arrive Date/Time:" value={leg.origgeoarrivaldatetime} />
                <Row label="Origin Depart Date/Time:" value={leg.origgeodepartdatetime} />
                <Row label="Dest Arrive Date/Time:" value={leg.destgeoarrivaldatetime} />
                <Row label="Dest Depart Date/Time:" value={leg.destgeodepartdatetime} />
              </div>
            </div>
          )}

          {showPaymentReq && (
            <div>
              <SectionHeader>Payment Requirements [gwiMove]</SectionHeader>
              <div className="grid grid-cols-2 gap-x-6">
                <div>
                  <div className="font-semibold text-label mb-0.5">Origin</div>
                  <div className="space-y-0.5">
                    <RequireSelectRow
                      label="Inspection:"
                      value={leg.origrequireinspection}
                      field="origrequireinspection"
                      legrecno={leg.legrecno}
                      editable={requireEditable}
                      onChange={onChange}
                      docRecno={leg.originspectionrecno}
                      onViewDocument={setDocViewRecno}
                    />
                    <RequireSelectRow
                      label="Interchange:"
                      value={leg.origrequireinterchange}
                      field="origrequireinterchange"
                      legrecno={leg.legrecno}
                      editable={requireEditable}
                      onChange={onChange}
                      docRecno={leg.originterchangerecno}
                      onViewDocument={setDocViewRecno}
                    />
                    <RequireSelectRow
                      label="BOL:"
                      value={leg.origrequirebol}
                      field="origrequirebol"
                      legrecno={leg.legrecno}
                      editable={requireEditable}
                      onChange={onChange}
                      docRecno={leg.origbolrecno}
                      onViewDocument={setDocViewRecno}
                    />
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-label mb-0.5">Destination</div>
                  <div className="space-y-0.5">
                    <RequireSelectRow
                      label="Interchange:"
                      value={leg.destrequireinterchange}
                      field="destrequireinterchange"
                      legrecno={leg.legrecno}
                      editable={requireEditable}
                      onChange={onChange}
                      docRecno={leg.destinterchangerecno}
                      onViewDocument={setDocViewRecno}
                    />
                    <RequireSelectRow
                      label="POD:"
                      value={leg.destrequirepod}
                      field="destrequirepod"
                      legrecno={leg.legrecno}
                      editable={requireEditable}
                      onChange={onChange}
                      docRecno={leg.destpodrecno}
                      onViewDocument={setDocViewRecno}
                    />
                    <RequireSelectRow
                      label="Scale Ticket:"
                      value={leg.destrequirescaleticket}
                      field="destrequirescaleticket"
                      legrecno={leg.legrecno}
                      editable={requireEditable}
                      onChange={onChange}
                      docRecno={leg.destscaleticketrecno}
                      onViewDocument={setDocViewRecno}
                    />
                  </div>
                </div>
              </div>
              {requireEditable && (
                <div className="mt-1 text-red-600 dark:text-red-400 text-[0.6875rem] italic">
                  Changes on this screen will not be saved unless you save the
                  order screen.
                </div>
              )}
            </div>
          )}

          {leg.orderIsGwiDispatcher && (
            <div>
              <SectionHeader>Driver Pay Information</SectionHeader>
              {hasPayLineDriver ? (
                <div className="space-y-1">
                  {canClearPay && (
                    <div className="flex justify-end pt-1 pr-1">
                      <PermissionsContainer
                        componentId="leg-driverpay-clear"
                        componentName="Remove Pay & Open Leg"
                        defaultHidden
                      >
                        <PermissionAwareButton
                          onClick={() => setShowClearDrawer(true)}
                          className="text-blue-600 dark:text-blue-400 hover:underline text-[0.6875rem] font-medium disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                        >
                          Remove Pay and Open Leg
                        </PermissionAwareButton>
                      </PermissionsContainer>
                    </div>
                  )}
                  <Row label="Rate:" value={leg.driverPayRate != null ? leg.driverPayRate.toFixed(2) : ''} />
                  <Row label="Fuel Type:" value={leg.driverPayFuelType} />
                  <Row label="Pay Status:" value={leg.driverPayStatus} />
                  <Row label="Pay Date:" value={leg.driverPayDate} />
                  <Row label="Approved By:" value={leg.driverPayApprovedBy} />
                  <Row label="Approved Date:" value={leg.driverPayApprovedDatetime} />
                </div>
              ) : requireEditable ? (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-muted">Do Not Pay:</span>
                    <div className="flex items-center gap-2">
                      <Toggle
                        checked={donotpay === 'Y'}
                        onChange={(c) => {
                          const v: 'Y' | 'N' = c ? 'Y' : 'N';
                          setDonotpay(v);
                          onChange?.(leg.legrecno, 'donotpayleg', v);
                        }}
                        size="sm"
                        ariaLabel="Do Not Pay"
                      />
                      <span className="text-heading">
                        {donotpay === 'Y' ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                  {donotpay !== ((leg.donotpayleg as 'Y' | 'N') || 'N') && (
                    <div className="mt-1 text-red-600 dark:text-red-400 text-[0.6875rem] italic">
                      Changes on this screen will not be saved unless you save the
                      order screen.
                    </div>
                  )}
                </>
              ) : (
                <Row label="Do Not Pay:" value={donotpay} />
              )}
            </div>
          )}
        </div>

        <div className="px-4 py-2.5 border-t border-gray-200 dark:border-gray-700 flex justify-center">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
      </div>

      {/* Reuse the tablemaint "Clear Leg" drawer verbatim — it self-fetches the
          pay line by recno and renders above this modal (Drawer is z-[60] vs the
          modal's z-50). On clear it persists immediately, so we refresh the pro
          (onPayCleared) and close this modal since its pay section is now stale. */}
      {showClearDrawer && !!leg.driverPayRecno && (
        <DriverPayOpenLegsDrawer
          row={{ recno: leg.driverPayRecno }}
          onClose={() => setShowClearDrawer(false)}
          onSaved={() => {
            setShowClearDrawer(false);
            onPayCleared?.();
            onClose();
          }}
        />
      )}

      {/* Leg-document View + Reject, launched from a require row's View icon. Renders above
          this modal (z-[70]). A successful reject persists immediately and flips the leg's
          require flag back to Y server-side, so we refresh the pro (onDocRejected) and close
          this modal since its snapshot is now stale. */}
      {docViewRecno != null && (
        <LegDocumentModal
          recno={docViewRecno}
          legnum={leg.legnum}
          canReject={canReject}
          onClose={() => setDocViewRecno(null)}
          onRejected={() => {
            setDocViewRecno(null);
            onDocRejected?.();
            onClose();
          }}
        />
      )}
    </>
  );
};

export default DispatchInfoModal;
